import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import JourneyListingUserDetailDto from "../../../../types/dtos/dashboards/manager/listings/journey/JourneyListingUserDetailDto";
import JourneyListingDto from "../../../../types/dtos/dashboards/manager/listings/journey/JourneyListingDto";
import { JourneyFormButtonArg } from "../../../../types/dashboard/JourneyFormDropDownItems";
import journeyFormHelper from "../../../../helpers/journeyFormHelper";
import DropDownPortalMenu, {
  DropDownMenuItem,
} from "../../../common/DropDownPortalMenu";

interface JourneyTableActionButtonsProps {
  journey: JourneyListingDto;
  user: JourneyListingUserDetailDto | undefined;
  hasPreventJourneySwitchConfigEnabled: boolean;
  onUpdateClick: (journey: JourneyListingDto) => void;
  onViewClick: (journey: JourneyListingDto) => void;
  onDualPrepItemClick: (
    user: JourneyListingUserDetailDto,
    journey: JourneyListingDto,
    arg: JourneyFormButtonArg
  ) => void;

  dropdownPortalMenuId: string;
  setPortalPosition: (position: { top: number; left: number }) => void;
}

const JourneyTableActionButtons: React.FC<JourneyTableActionButtonsProps> = ({
  journey,
  user,
  hasPreventJourneySwitchConfigEnabled,
  onUpdateClick,
  onViewClick,
  onDualPrepItemClick,
  dropdownPortalMenuId,
  setPortalPosition,
}) => {
  const { t } = useTranslation();
  const [hasButtonBeenClicked, setHasButtonBeenClicked] = useState(false);

  // Early return if no user, but AFTER all hooks have been called
  if (!user) {
    return null;
  }

  // Determine if the journey is completed
  const isCompleted = journey.overallStatus === "COMPLETED";
  const isDualPrepJourney = journey.isDualPrepJourney || false;

  // Calculate button text based on status
  const standardButtonText = isCompleted
    ? t("Common.View")
    : t("Common.Update");

  // Logic for determining if button should be disabled
  const journeyNotStartedOrPartiallyCompleteStatus =
    journey.hasNotBeenStarted ||
    (journey.approvalStatusEnum !== null &&
      journey.approvalStatusEnum === "PARTIALLY-SAVED-JOURNEY");

  // Disable button if user hasn't started the journey
  const disableStandardButton = journeyNotStartedOrPartiallyCompleteStatus;

  // Get dual prep dropdown items
  const dualPrepDropDownButtonItems =
    journeyFormHelper.getDualPrepDropdownButtonItems(
      journey,
      isCompleted,
      true, // isYourPeoplePage
      hasPreventJourneySwitchConfigEnabled
    );

  // Handle standard button click for non-dual prep
  const handleStandardButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setHasButtonBeenClicked(true);
    if (isCompleted) {
      onViewClick(journey);
    } else {
      onUpdateClick(journey);
    }
  };

  // Handle dual prep button click - for DropDownMenu
  const handleDualPrepMenuItemClick = (itemIndex: number, customArg: any) => {
    setHasButtonBeenClicked(true);
    onDualPrepItemClick(user, journey, customArg);
  };

  // For non-dual prep journeys, render a standard button
  if (!isDualPrepJourney) {
    return (
      <button
        onClick={handleStandardButtonClick}
        disabled={disableStandardButton || hasButtonBeenClicked}
        className="btn-primary text-sm py-1 px-4 w-full text-center disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {hasButtonBeenClicked ? (
          <span className="flex items-center justify-center">
            <span>{t("Common.PleaseWait")}</span>
          </span>
        ) : (
          standardButtonText
        )}
      </button>
    );
  }

  // For dual prep journeys
  if (isDualPrepJourney && dualPrepDropDownButtonItems.length >= 1) {
    // Convert journey form button items to DropDownMenuItems
    const dropdownItems: DropDownMenuItem[] = dualPrepDropDownButtonItems.map(
      (item) => ({
        text: item.text,
        customArg: item.customArg,
        disabled: item.disabled,
      })
    );

    // Determine the simplified button text based on the first item's text
    const firstItemText = dualPrepDropDownButtonItems[0].text;
    let buttonText = firstItemText;

    // If the first item contains "View" or "Update", show the simplified version
    if (firstItemText.includes(t("Common.View"))) {
      buttonText = t("Common.View");
    } else if (
      firstItemText.includes(t("Common.Update")) ||
      firstItemText.includes(t("Common.CompletePlanning"))
    ) {
      buttonText = t("Common.Update");
    }

    // If the button has been clicked, show a "Please Wait" button
    if (hasButtonBeenClicked) {
      return (
        <button
          className="btn-primary text-sm py-1 px-4 w-full text-center disabled:opacity-50 disabled:cursor-not-allowed"
          disabled
        >
          <span className="flex items-center justify-center">
            <span>{t("Common.PleaseWait")}</span>
          </span>
        </button>
      );
    }

    // Create the portal container div
    return (
      <>
        <DropDownPortalMenu
          id={dropdownPortalMenuId}
          eventType="EVENT"
          items={dropdownItems}
          onItemClick={handleDualPrepMenuItemClick}
          menuButtonClassName="btn-primary text-sm py-1 px-4 w-full text-center"
          outerDivClassName="relative inline-block text-left w-full"
          setPortalPosition={setPortalPosition}
          offsetPortalPosTop={5}
          offsetPortalPosLeft={77}
          defaultButtonTextForOneItem={buttonText}
          customButtonContents={
            <span className="flex items-center justify-between w-full">
              <span className="flex-grow text-center">{buttonText}</span>
              <FontAwesomeIcon
                icon={faChevronDown}
                size="sm"
                className="ml-2"
              />
            </span>
          }
        />
      </>
    );
  }

  return null;
};

export default JourneyTableActionButtons;
